/**
 * Display helpers for pprov_communication timestamps.
 *
 * Prefer `msgTimestamp` (has a clock). `msgDate` is DATE-only and has no time.
 * Server / MySQL clock is UTC; naive "YYYY-MM-DD HH:MM:SS" strings are treated
 * as UTC (same pattern as invite_sent_at in MyAccount) so the browser can
 * convert to the viewer's local timezone.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a message timestamp into a Date, or null if unusable. */
export function parseMessageInstant(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // Date-only → local midnight (avoid UTC day-shift for west-of-Greenwich).
  if (DATE_ONLY.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    const local = new Date(y, m - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  let iso = s.replace(" ", "T");
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) {
    iso += "Z";
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function localDayDiff(from, to) {
  return Math.round((startOfLocalDay(to) - startOfLocalDay(from)) / 86400000);
}

function formatLocalTime(date) {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatLocalDate(date) {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Relative day label + local time when a clock is available.
 * Accepts a message row `{ msgTimestamp, msgDate }` or a raw timestamp string.
 */
export function formatMessageWhen(messageOrRaw, now = new Date()) {
  let raw = null;
  let hasClock = false;

  if (messageOrRaw && typeof messageOrRaw === "object") {
    if (messageOrRaw.msgTimestamp) {
      raw = messageOrRaw.msgTimestamp;
      hasClock = true;
    } else {
      raw = messageOrRaw.msgDate;
    }
  } else {
    raw = messageOrRaw;
    hasClock = !!raw && !DATE_ONLY.test(String(raw).trim());
  }

  const date = parseMessageInstant(raw);
  if (!date) return "";

  const dayDiff = localDayDiff(date, now);
  let dayLabel;
  if (dayDiff === 0) dayLabel = "Today";
  else if (dayDiff === 1) dayLabel = "Yesterday";
  else if (dayDiff >= 2 && dayDiff <= 6) dayLabel = `${dayDiff} days ago`;
  else dayLabel = formatLocalDate(date);

  if (!hasClock) return dayLabel;
  return `${dayLabel} · ${formatLocalTime(date)}`;
}
